import { ComponentRef } from '@angular/core';
import { ActivatedRouteSnapshot, RouteReuseStrategy, DetachedRouteHandle } from '@angular/router';
import { ReuseStrategyEnum } from '../enum/reuseStrategy.enum';
import { ScrollPositionService } from '../scroll.service';
export class CustomReuseStrategy implements RouteReuseStrategy {
    constructor(private scrollService: ScrollPositionService) { }
    private handles = new Map<string, { handle: DetachedRouteHandle; scrollPosition: number }>();
    private currentReuseRoutePrelix: string[] = [];
    private shouldResetReuseStrategy(route: ActivatedRouteSnapshot): boolean {
        let snapshot: ActivatedRouteSnapshot = route;
        while (snapshot.children && snapshot.children.length) {
            snapshot = snapshot.children[0];
        }
        let conditionResetKey = snapshot.data && snapshot.data['reuseKey'] === ReuseStrategyEnum.RESET;
        let conditionPath = this.currentReuseRoutePrelix.length == 0 ? false :
            !this.currentReuseRoutePrelix.some(x => this.getKey(route).startsWith(x, 0));
        return conditionResetKey || conditionPath;
    }

    private shouldIgnore(route: ActivatedRouteSnapshot): boolean {
        return !!route.outlet && route.outlet !== 'primary'
    }

    private deactivateAllHandles(): void {
        this.handles.forEach((handle: DetachedRouteHandle) => this.destroyComponent(handle));
        this.handles.clear();
    }

    private destroyComponent(handle: DetachedRouteHandle): void {
        const componentRef: ComponentRef<any> = (handle as any)?.componentRef;
        if (componentRef) {
            componentRef.destroy();
        }
    }

    private getKey(route: ActivatedRouteSnapshot): string {
        return route.pathFromRoot
            .map((snapshot: ActivatedRouteSnapshot) => snapshot.routeConfig ? snapshot.routeConfig.path : '')
            .filter(path => (path || '').length > 0)
            .join('/');
    }

    // Decides if the route should be stored
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        const isReused = route.data['reuseKey'] === ReuseStrategyEnum.REUSED;
        if (isReused) {
            this.currentReuseRoutePrelix = route.data['reuseRoutePrelix'] || []
        }
        return isReused;
    }

    //Store the information for the route we're destructing
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
        if (!handle) {
            return;
        }
        const scrollPosition = window.scrollY || 0;
        this.handles.set(this.getKey(route), { handle, scrollPosition });
    }

    //Return true if we have a stored route object for the next route
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (this.shouldIgnore(route)) {
            return false;
        }
        if (this.shouldResetReuseStrategy(route)) {
            this.deactivateAllHandles();
            return false;
        }

        return this.handles.has(this.getKey(route));
    }

    //If we returned true in shouldAttach(), now return the actual route data for restoration
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        const handler = this.handles.get(this.getKey(route));
        if (handler) {
            const { scrollPosition, handle } = handler;
            this.scrollService.setScrollPosition(scrollPosition);
            return handle;
        }
        return null;
    }

    //Reuse the route if we're going to and from the same route
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        return future.routeConfig === curr.routeConfig;
    }
}